import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Chip,
  Button,
  List,
  ListItem,
  ListItemText,
  Card,
  CardContent,
  TextField,
  IconButton,
  Tooltip,
  Stack,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../store/store';
import { fetchBillDetail } from '../../store/slices/billsSlice';
import { apiService } from '../../services/apiService';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../../context/AuthContext';
import AdSlot from '../../components/Common/AdSlot';
import BillHero, { ReadingMode } from '../../components/Bills/BillHero';
import VoteCard from '../../components/Votes/VoteCard';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`bill-tabpanel-${index}`}
      aria-labelledby={`bill-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3, overflowX: 'auto', maxWidth: '100%' }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const BillDetail: React.FC = () => {
  // Utility to insert or update meta tags for SEO without extra deps
  const upsertMeta = (selector: { name?: string; property?: string; id?: string }, content: string) => {
    let el: HTMLMetaElement | null = null;
    if (selector.id) {
      el = document.querySelector(`meta#${selector.id}`) as HTMLMetaElement | null;
    }
    if (!el && selector.name) {
      el = document.querySelector(`meta[name='${selector.name}']`) as HTMLMetaElement | null;
    }
    if (!el && selector.property) {
      el = document.querySelector(`meta[property='${selector.property}']`) as HTMLMetaElement | null;
    }
    if (!el) {
      el = document.createElement('meta');
      if (selector.id) el.id = selector.id;
      if (selector.name) el.setAttribute('name', selector.name);
      if (selector.property) el.setAttribute('property', selector.property);
      document.head.appendChild(el);
    }
    el.setAttribute('content', content);
    return el;
  };

  const removeIfExists = (selector: string) => {
    const el = document.querySelector(selector);
    if (el && el.parentNode) el.parentNode.removeChild(el);
  };
  const { congress, billType, billNumber } = useParams<{
    congress: string;
    billType: string;
    billNumber: string;
  }>();

  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { currentBill, loading, error } = useSelector((state: RootState) => state.bills);
  // Local state: when detail fetch returns 404/not-found transiently, we retry like the 202-queued path
  const [transientRetrying, setTransientRetrying] = useState(false);
  const [transientRetries, setTransientRetries] = useState(0);

  const [tabValue, setTabValue] = useState(0);
  const [billActions, setBillActions] = useState<any>(null);
  const [billAmendments, setBillAmendments] = useState<any>(null);
  const [billText, setBillText] = useState<any>(null);
  const [billSummaries, setBillSummaries] = useState<any>(null);
  const [billCommittees, setBillCommittees] = useState<any>(null);
  const [billRelatedBills, setBillRelatedBills] = useState<any>(null);
  const [billSubjects, setBillSubjects] = useState<any>(null);
  const [billTitles, setBillTitles] = useState<any>(null);
  const [billCosponsors, setBillCosponsors] = useState<any>(null);
  const [aiGenLoading, setAiGenLoading] = useState<boolean>(false);
  const [aiGenError, setAiGenError] = useState<string | null>(null);
  const { authenticated, login } = useAuth();
  const [readingMode, setReadingMode] = useState<ReadingMode>('default');
  // Notes (bill)
  const [noteText, setNoteText] = useState<string>('');
  const [noteSaved, setNoteSaved] = useState<string | null>(null);
  const [noteError, setNoteError] = useState<string | null>(null);

  // Load poll summary when route changes
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!congress || !billType || !billNumber) return;
      setPollError(null);
      setPollLoading(true);
      try {
        const resp = await apiService.getBillPollSummary(parseInt(congress), billType, billNumber);
        if (cancelled) return;
        setPoll(resp?.data || resp);
      } catch (e: any) {
        if (cancelled) return;
        // Hide widget when 404 or feature disabled (backend returns 404)
        const status = e?.response?.status;
        if (status === 404) {
          setPoll(null);
        } else {
          setPollError(e?.message || 'Failed to load poll');
        }
      } finally {
        if (!cancelled) setPollLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [congress, billType, billNumber, authenticated]);

  // Notes: load/save from localStorage per bill key
  useEffect(() => {
    try {
      if (!currentBill) return;
      const key = `bill:note:${currentBill.congress}:${(currentBill.bill_type || currentBill.type || '').toLowerCase()}:${currentBill.bill_number || currentBill.number}`;
      const saved = localStorage.getItem(key);
      if (saved !== null) setNoteText(saved);
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBill?.congress, (currentBill as any)?.bill_type, (currentBill as any)?.type, (currentBill as any)?.bill_number, (currentBill as any)?.number]);

  useEffect(() => {
    let t: any;
    try {
      if (!currentBill) return;
      const key = `bill:note:${currentBill.congress}:${(currentBill.bill_type || currentBill.type || '').toLowerCase()}:${currentBill.bill_number || currentBill.number}`;
      t = setTimeout(() => {
        try {
          localStorage.setItem(key, noteText || '');
          if (noteText.trim()) setNoteSaved('Draft saved on this device');
        } catch (e: any) {
          setNoteError('Failed to save note');
        }
      }, 500);
    } catch {}
    return () => t && clearTimeout(t);
  }, [noteText, currentBill]);
  const [isFollowing, setIsFollowing] = useState<boolean | null>(null);
  const [isBookmarked, setIsBookmarked] = useState<boolean | null>(null);
  const [summaryMode, setSummaryMode] = useState<'default'|'eli5'|'expert'|'pros_cons'>('default');
  // Diff state
  const [diffVersionA, setDiffVersionA] = useState<string>('');
  const [diffVersionB, setDiffVersionB] = useState<string>('');
  const [diffMode, setDiffMode] = useState<'lines'|'words'>('lines');
  const [diffLoading, setDiffLoading] = useState<boolean>(false);
  const [diffError, setDiffError] = useState<string | null>(null);
  const [diffResult, setDiffResult] = useState<any | null>(null);
  // Social: reactions and comments
  const [reactionSummary, setReactionSummary] = useState<Record<string, number> | null>(null);
  // Embed widget UI
  const [showEmbed, setShowEmbed] = useState<boolean>(false);
  const [comments, setComments] = useState<any[] | null>(null);
  const [commentBody, setCommentBody] = useState<string>('');
  const [commentPosting, setCommentPosting] = useState<boolean>(false);
  // Community Poll
  const [poll, setPoll] = useState<any | null>(null);
  const [pollLoading, setPollLoading] = useState<boolean>(false);
  const [pollError, setPollError] = useState<string | null>(null);
  const [socialUnavailable, setSocialUnavailable] = useState<{ comments?: boolean; reactions?: boolean }>({});

  // Sticky hero eligibility based on visible viewport height vs. hero height
  const heroRef = React.useRef<HTMLDivElement | null>(null);
  const [stickyOk, setStickyOk] = useState<boolean>(true);
  useEffect(() => {
    // Debounced recompute to avoid thrash on resize/content changes
    let raf = 0;
    const recompute = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        try {
          const h = heroRef.current?.offsetHeight || 0;
          const vh = window.innerHeight || 0;
          // Only allow sticky if hero occupies less than 45% of the viewport height
          setStickyOk(h > 0 && vh > 0 ? h < vh * 0.45 : true);
        } catch {
          setStickyOk(true);
        }
      });
    };
    recompute();

    // Listen to window resizes
    window.addEventListener('resize', recompute);

    // Observe hero content mutations (async content, line wraps)
    let observer: MutationObserver | null = null;
    if (heroRef.current && typeof MutationObserver !== 'undefined') {
      observer = new MutationObserver(() => recompute());
      observer.observe(heroRef.current, { childList: true, subtree: true, characterData: true, attributes: true });
    }

    // Also recompute when fonts are ready which can change line wrapping
    if ((document as any).fonts && typeof (document as any).fonts.addEventListener === 'function') {
      (document as any).fonts.addEventListener('loadingdone', recompute);
    }

    return () => {
      window.removeEventListener('resize', recompute);
      if (observer) observer.disconnect();
      cancelAnimationFrame(raf);
      if ((document as any).fonts && typeof (document as any).fonts.removeEventListener === 'function') {
        (document as any).fonts.removeEventListener('loadingdone', recompute);
      }
    };
    // re-evaluate when bill changes
  }, [currentBill]);

  useEffect(() => {
    if (congress && billType && billNumber) {
      dispatch(fetchBillDetail({
        congress: parseInt(congress),
        billType,
        billNumber,
      }));
    }
  }, [dispatch, congress, billType, billNumber]);

  // Poll for detail only when user triggered summary generation or when backend reports queued
  useEffect(() => {
    if (!congress || !billType || !billNumber) return;
    if (!aiGenLoading) return;
    const id = setInterval(() => {
      dispatch(fetchBillDetail({
        congress: parseInt(congress),
        billType: billType,
        billNumber: billNumber,
      }));
    }, 3000);
    return () => clearInterval(id);
  }, [aiGenLoading, dispatch, congress, billType, billNumber]);

  // If initial fetch errored with a Not Found (404) or 'not found' message, treat it as transient and poll briefly
  useEffect(() => {
    if (!congress || !billType || !billNumber) return;
    const msg = (error || '').toString().toLowerCase();
    const looksNotFound = !!error && (msg.includes('404') || msg.includes('not found'));
    if (!looksNotFound) {
      // reset transient retry state when error clears or different error
      if (transientRetrying) {
        setTransientRetrying(false);
        setTransientRetries(0);
      }
      return;
    }
    if (transientRetries >= 15) return; // ~30s with 2s interval max
    setTransientRetrying(true);
    const id = setInterval(() => {
      setTransientRetries((n) => n + 1);
      dispatch(fetchBillDetail({
        congress: parseInt(congress),
        billType: billType,
        billNumber: billNumber,
      }));
    }, 2000);
    return () => clearInterval(id);
  }, [error, dispatch, congress, billType, billNumber, transientRetries, transientRetrying]);

  // If backend responded with a queued status (202 -> {detail: 'Bill queued...'}), poll until available
  useEffect(() => {
    if (!congress || !billType || !billNumber) return;
    const isQueued = currentBill && typeof (currentBill as any).detail === 'string' && (currentBill as any).detail.toLowerCase().includes('queued');
    if (!isQueued) return;
    const id = setInterval(() => {
      dispatch(fetchBillDetail({
        congress: parseInt(congress),
        billType: billType,
        billNumber: billNumber,
      }));
    }, 2000);
    return () => clearInterval(id);
  }, [currentBill, dispatch, congress, billType, billNumber]);

  // Prefetch actions, amendments, text, summaries, subjects, titles, related bills, committees, and cosponsors
  useEffect(() => {
    if (currentBill) {
      if (!billActions) fetchAdditionalData('actions');
      if (!billAmendments) fetchAdditionalData('amendments');
      if (!billText) fetchAdditionalData('text');
      if (!billSummaries) fetchAdditionalData('summaries');
      if (!billSubjects) fetchAdditionalData('subjects');
      if (!billTitles) fetchAdditionalData('titles');
      if (!billRelatedBills) fetchAdditionalData('relatedbills');
      if (!billCommittees) fetchAdditionalData('committees');
      if (!billCosponsors) fetchAdditionalData('cosponsors');
      // Social fetches
      const bid = (currentBill as any)?.bill_id;
      if (bid && socialUnavailable.reactions !== true) {
        apiService.getReactionSummary('bill', Number(bid)).then(r => setReactionSummary((r.data && r.data.summary) || {})).catch(e => {
          if (e?.response?.status === 404) setSocialUnavailable(prev => ({ ...prev, reactions: true }));
        });
      }
      if (bid && socialUnavailable.comments !== true) {
        apiService.listComments(Number(bid)).then(r => setComments(Array.isArray(r.data) ? r.data : (Array.isArray(r.data?.items) ? r.data.items : [])))
          .catch(e => {
            if (e?.response?.status === 404) setSocialUnavailable(prev => ({ ...prev, comments: true }));
          });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBill]);

  // SEO: set document title, meta description, social tags, and JSON-LD structured data
  useEffect(() => {
    if (!currentBill) return;
    const billId = `${currentBill.bill_type || currentBill.type || billType?.toUpperCase()} ${currentBill.bill_number || currentBill.number || billNumber}`.trim();
    const baseTitle = currentBill.title || currentBill.short_title || billId;
    const pageTitle = `${baseTitle} — AskTheBill.ai`;
    const rawDesc = (currentBill.summary_text || currentBill.ai_summary || currentBill.latest_action_text || '').toString();
    const description = rawDesc.length > 0 ? (rawDesc.length > 300 ? rawDesc.slice(0, 297) + '…' : rawDesc) : `Explore details, actions, sponsors, and summaries for ${billId} on AskTheBill.ai.`;

    document.title = pageTitle;
    upsertMeta({ name: 'description' }, description);

    const url = typeof window !== 'undefined' ? window.location.href : `https://askthebill.ai/bills/${congress}/${billType}/${billNumber}`;

    // Open Graph
    upsertMeta({ property: 'og:title' }, pageTitle);
    upsertMeta({ property: 'og:description' }, description);
    upsertMeta({ property: 'og:type' }, 'article');
    upsertMeta({ property: 'og:url' }, url);

    // Twitter Cards
    upsertMeta({ name: 'twitter:card' }, 'summary');
    upsertMeta({ name: 'twitter:title' }, pageTitle);
    upsertMeta({ name: 'twitter:description' }, description);

    // JSON-LD Schema.org for Legislation
    // Clean previous if present
    removeIfExists("script#bill-seo-ld");
    const ld: any = {
      '@context': 'https://schema.org',
      '@type': 'Legislation',
      name: baseTitle,
      identifier: billId,
      url,
      description,
      dateCreated: currentBill.introduced_date || undefined,
      legislationIdentifier: billId,
      legislationJurisdiction: 'US',
      isPartOf: currentBill.congress ? `${currentBill.congress}th Congress` : undefined,
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'bill-seo-ld';
    script.text = JSON.stringify(ld);
    document.head.appendChild(script);

    // Cleanup on unmount or bill change
    return () => {
      // Note: we do not reset document.title to avoid flicker
      removeIfExists("script#bill-seo-ld");
    };
  }, [currentBill, billType, billNumber, congress]);

  // Normalize AI summary text to ensure Markdown renders correctly
  const normalizeSummary = (input: string): string => {
    if (!input) return '';
    let s = String(input).replace(/\r\n/g, '\n');
    // Trim outer whitespace
    s = s.trim();
    const lines = s.split('\n');
    // Compute common indentation among non-empty lines (spaces only)
    let minIndent = Infinity;
    for (const line of lines) {
      if (!line.trim()) continue;
      const m = line.match(/^(\s+)/);
      if (m) {
        // count spaces, but treat tabs as 2 spaces equivalent for safety
        const raw = m[1];
        const count = raw.replace(/\t/g, '  ').length;
        if (count < minIndent) minIndent = count;
      } else {
        minIndent = 0;
        break;
      }
    }
    if (!isFinite(minIndent)) minIndent = 0;
    const deindented = lines.map((ln) => {
      let l = ln;
      // Remove common indent
      if (minIndent > 0) {
        // Replace tabs with 2 spaces when slicing to remain consistent with count
        const prefix = l.match(/^(\s*)/);
        if (prefix) {
          let toRemove = minIndent;
          let i = 0;
          while (toRemove > 0 && i < l.length && /\s/.test(l[i])) {
            if (l[i] === '\\t') toRemove -= 2; else toRemove -= 1;
            i++;
          }
          l = l.slice(i);
        }
      }
      // Left-trim small stray spaces to enable markdown markers (###, -, 1.)
      l = l.replace(/^\s{1,3}([#\-\d])/,'$1');
      return l;
    });

    // Promote a leading "Title:" line to a heading if present
    if (deindented.length && /^\s*title\s*:/i.test(deindented[0])) {
      deindented[0] = '### ' + deindented[0].replace(/^\s*title\s*:\s*/i, '');
    }

    // Collapse excessive blank lines (max 2)
    const collapsed: string[] = [];
    let blankRun = 0;
    for (const ln of deindented) {
      if (ln.trim() === '') {
        blankRun++;
        if (blankRun <= 2) collapsed.push('');
      } else {
        blankRun = 0;
        collapsed.push(ln);
      }
    }

    return collapsed.join('\n').trim();
  };

  const fetchAdditionalData = async (type: 'actions' | 'amendments' | 'text' | 'summaries' | 'subjects' | 'titles' | 'relatedbills' | 'committees' | 'cosponsors') => {
    if (!congress || !billType || !billNumber) return;

    try {
      let response;
      switch (type) {
        case 'actions':
          response = await apiService.getBillActions(parseInt(congress), billType, billNumber);
          setBillActions(response.data);
          break;
        case 'amendments':
          response = await apiService.getBillAmendments(parseInt(congress), billType, billNumber);
          setBillAmendments(response.data);
          break;
        case 'text':
          response = await apiService.getBillText(parseInt(congress), billType, billNumber);
          setBillText(response.data);
          break;
        case 'summaries':
          response = await apiService.getBillSummaries(parseInt(congress), billType, billNumber);
          setBillSummaries(response.data);
          break;
        case 'subjects':
          response = await apiService.getBillSubjects(parseInt(congress), billType, billNumber);
          setBillSubjects(response.data);
          break;
        case 'titles':
          response = await apiService.getBillTitles(parseInt(congress), billType, billNumber);
          setBillTitles(response.data);
          break;
        case 'relatedbills':
          response = await apiService.getBillRelatedBills(parseInt(congress), billType, billNumber);
          setBillRelatedBills(response.data);
          break;
        case 'committees':
          response = await apiService.getBillCommittees(parseInt(congress), billType, billNumber);
          setBillCommittees(response.data);
          break;
        case 'cosponsors':
          response = await apiService.getBillCosponsors(parseInt(congress), billType, billNumber, { limit: 250, offset: 0 });
          setBillCosponsors(response.data);
          break;
      }
    } catch (error) {
      console.error(`Failed to fetch bill ${type}:`, error);
      // Prevent indefinite spinners by setting an empty state on failure
      switch (type) {
        case 'actions':
          setBillActions({ actions: [] });
          break;
        case 'amendments':
          setBillAmendments({ amendments: [] });
          break;
        case 'text':
          setBillText({ textVersions: [], html: '', text: '', pdf: '' });
          break;
        case 'summaries':
          setBillSummaries({ summaries: [] });
          break;
        case 'subjects':
          setBillSubjects({ subjects: { legislativeSubjects: [], policyArea: null } });
          break;
        case 'titles':
          setBillTitles({ titles: [] });
          break;
        case 'relatedbills':
          setBillRelatedBills({ relatedBills: [] });
          break;
        case 'committees':
          setBillCommittees({ committees: [] });
          break;
        case 'cosponsors':
          setBillCosponsors({ cosponsors: [] });
          break;
      }
    }
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);

    // Fetch additional data when tabs are accessed
    if (newValue === 1 && !billActions) {
      fetchAdditionalData('actions');
    } else if (newValue === 2 && !billAmendments) {
      fetchAdditionalData('amendments');
    } else if (newValue === 3 && !billText) {
      fetchAdditionalData('text');
    } else if (newValue === 4 && !billSummaries) {
      fetchAdditionalData('summaries');
    } else if (newValue === 5 && !billSubjects) {
      fetchAdditionalData('subjects');
    } else if (newValue === 6 && !billTitles) {
      fetchAdditionalData('titles');
    } else if (newValue === 7 && !billRelatedBills) {
      fetchAdditionalData('relatedbills');
    } else if (newValue === 8 && !billCommittees) {
      fetchAdditionalData('committees');
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading bill details..." />;
  }

  const isQueued = currentBill && typeof (currentBill as any).detail === 'string' && (currentBill as any).detail.toLowerCase().includes('queued');
  if (isQueued) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/bills')}>
          Back to Bills
        </Button>
        <LoadingSpinner message="Preparing bill data... This page will refresh automatically." />
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          We just queued this bill for ingestion. Please wait a few seconds.
        </Typography>
      </Box>
    );
  }

  if (transientRetrying && !currentBill) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/bills')}>
          Back to Bills
        </Button>
        <LoadingSpinner message="Preparing bill data... This page will refresh automatically." />
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          We’re fetching the bill details. This can take a few seconds the first time it’s viewed.
        </Typography>
      </Box>
    );
  }

  if (!currentBill) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/bills')}>
          Back to Bills
        </Button>
        <Typography variant="h6" sx={{ mt: 2 }}>
          Bill not found
        </Typography>
      </Box>
    );
  }

  const actionTexts = ((billActions?.actions || []) as any[]).map(a =>
    String(a.text || a.action_text || a.actionText || a.description || '').toLowerCase());
  const latestText = String(currentBill.latest_action_text || currentBill.latest_action || '').toLowerCase();
  const hasAction = (phrases: string[]) => [...actionTexts, latestText].some(text => phrases.some(p => text.includes(p)));
  const billStage = hasAction(['became law', 'became public law', 'signed by president']) ? 'Became law'
    : hasAction(['passed the senate', 'passed/agreed to in senate', 'passed senate']) ? 'Passed Senate'
    : hasAction(['passed the house', 'passed/agreed to in house', 'passed house']) ? 'Passed House'
    : hasAction(['referred', 'committee']) ? 'In committee'
    : currentBill.introduced_date ? 'Introduced' : 'Status unavailable';

  return (
    <Box sx={{ display: 'flow-root', isolation: 'isolate', position: 'relative' }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/bills')} sx={{ mb: 1, mt: 1 }} aria-label="Back to all bills">
        Back to Bills
      </Button>
      <Box component="nav" aria-label="Breadcrumb" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <Button onClick={() => navigate('/bills')} variant="text" size="small">Bills</Button>
          <span aria-hidden="true"> / </span>
          <span>{`${(currentBill.bill_type || currentBill.type || '').toUpperCase()} ${currentBill.bill_number || currentBill.number}`}</span>
        </Typography>
      </Box>

      <div ref={heroRef as any}>
      <BillHero
        code={`${(currentBill.bill_type || currentBill.type || '').toUpperCase()} ${currentBill.bill_number || currentBill.number}`}
        title={currentBill.title}
        statusBadge={billStage}
        stepStates={(() => {
          const actions = (billActions && Array.isArray(billActions.actions)) ? billActions.actions : [];
          const find = (preds: string[]) => actions.find((a: any) => preds.some(p => ((a.text||a.action_text||a.actionText||a.description||'').toLowerCase()).includes(p)));
          const introduced = actions.length > 0 || Boolean(currentBill.introduced_date);
          const house = !!find(['passed the house', 'passed/agreed to in house', 'passed house']);
          const senate = !!find(['passed the senate', 'passed/agreed to in senate', 'passed senate']);
          const prez = !!find(['became law', 'became public law', 'signed by president']);
          return [introduced, house, senate, prez];
        })()}
        progressPct={(() => {
          const actions = (billActions && Array.isArray(billActions.actions)) ? billActions.actions : [];
          const find = (preds: string[]) => actions.find((a: any) => preds.some(p => ((a.text||a.action_text||a.actionText||a.description||'').toLowerCase()).includes(p)));
          const introduced = actions.length > 0 || currentBill.introduced_date ? true : false;
          const house = !!find(['passed the house', 'passed/agreed to in house', 'passed house']);
          const senate = !!find(['passed the senate', 'passed/agreed to in senate', 'passed senate']);
          const prez = !!find(['became law', 'became public law', 'signed by president']);
          const steps = [introduced, house, senate, prez];
          return Math.round((steps.filter(Boolean).length / steps.length) * 100);
        })()}
        metadata={[
          { label: `Congress ${currentBill.congress}` },
          { label: (currentBill.bill_type || currentBill.type || '').toUpperCase(), variant: 'outlined' },
        ]}
        isFollowing={isFollowing}
        onToggleFollow={async () => {
          const canAct = authenticated;
          const doLogin = login;
          const c = parseInt(congress as string);
          const t = String(billType);
          const n = String(billNumber);
          if (!canAct) { doLogin(); return; }
          try {
            if (isFollowing) {
              await apiService.unfollowBillByKey(c, t, n);
              setIsFollowing(false);
            } else {
              await apiService.followBillByKey(c, t, n);
              setIsFollowing(true);
            }
          } catch (e) { /* no-op */ }
        }}
        onChat={() => {
          const type = (currentBill.bill_type || currentBill.type || '').toLowerCase();
          const number = String(currentBill.bill_number || currentBill.number || '').trim();
          navigate('/chat', { state: { billContext: [`${currentBill.congress}:${type}:${number}`] } });
        }}
        onShare={() => {
          const url = window.location.href;
          if ((navigator as any).share) {
            (navigator as any).share({ title: document.title, url });
          } else {
            navigator.clipboard?.writeText(url);
          }
        }}
        onCompare={() => {
          const usp = new URLSearchParams();
          usp.set('a', `${currentBill.congress}/${(currentBill.bill_type||currentBill.type)}/${currentBill.bill_number||currentBill.number}`);
          navigate({ pathname: '/compare/bills', search: `?${usp.toString()}` });
        }}
        onRefresh={async () => {
          try {
            await apiService.getBillDetail(parseInt(congress as string), billType as string, billNumber as string, { forceRefresh: true });
            await dispatch(fetchBillDetail({ congress: parseInt(congress as string), billType: billType as string, billNumber: billNumber as string }));
            await fetchAdditionalData('text');
            await fetchAdditionalData('summaries');
          } catch (e) {
            console.error('Failed to refresh bill', e);
          }
        }}
        onGenerateSummary={async () => {
          if (!authenticated) { login(); return; }
          if (!congress || !billType || !billNumber) return;
          try {
            setAiGenError(null);
            setAiGenLoading(true);
            const mode = readingMode === 'proscons' ? 'pros_cons' : readingMode;
            const opts = mode === 'default' ? {} : { mode } as any;
            const resp = await apiService.postBillAISummary(parseInt(congress as string), billType as string, billNumber as string, opts);
            // If the server queued the job (202), start a short polling loop until the summary appears
            if (resp && resp.status === 202) {
              // Optimistically refresh once
              const c = parseInt(congress as string);
              const t = billType as string;
              const n = billNumber as string;
              const pollUntil = Date.now() + 45000; // up to 45s
              let found = false;
              while (Date.now() < pollUntil) {
                try {
                  const r2 = await apiService.getBillDetail(c, t, n);
                  const b = r2?.data || {};
                  if (b && (b.ai_summary || b.summary_text)) {
                    found = true;
                    break;
                  }
                } catch {}
                await new Promise(res => setTimeout(res, 3000));
              }
              // Update redux detail either way; UI will render when ready
              await dispatch(fetchBillDetail({ congress: parseInt(congress as string), billType: billType as string, billNumber: billNumber as string }));
              if (!found) {
                setAiGenError('Summary request queued. It will appear shortly; you can continue browsing.');
              }
            } else {
              // Inline success path: refresh detail to show new summary
              await dispatch(fetchBillDetail({ congress: parseInt(congress as string), billType: billType as string, billNumber: billNumber as string }));
            }
          } catch (e: any) {
            console.error('Failed to generate AI bill summary', e);
            // Map common proxy timeouts to a friendlier message
            const msg = (e?.response?.status === 504 || e?.code === 'ERR_BAD_RESPONSE')
              ? 'The AI engine is busy. Your request has been queued and will complete shortly.'
              : (e?.message || 'Failed to generate AI summary');
            setAiGenError(msg);
          } finally {
            setAiGenLoading(false);
          }
        }}
        onMoreAction={(action) => {
          if (!congress || !billType || !billNumber) return;
          if (action === 'embed') {
            setShowEmbed(v => !v);
            return;
          }
          const c = parseInt(congress as string);
          const t = billType as string;
          const n = billNumber as string;
          let url = '';
          if (action === 'json') url = apiService.getBillExportJsonUrl(c, t, n);
          if (action === 'csv') url = apiService.getBillExportCsvUrl(c, t, n);
          if (action === 'rss') url = apiService.getBillRssUrl(c, t, n);
          if (url) window.open(url, '_blank');
        }}
        readingMode={readingMode}
        onReadingModeChange={(m) => {
          setReadingMode(m);
          const map = (mm: ReadingMode) => mm === 'proscons' ? 'pros_cons' : mm;
          // @ts-ignore
          setSummaryMode(map(m));
        }}
      />
      </div>

      {showEmbed && (
        <Paper sx={{ p: 2, mt: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>Embed This Bill</Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Copy and paste this snippet into your site to embed a compact Bill Status card.
          </Typography>
          <Box sx={{ position: 'relative' }}>
            <Box component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, \"Liberation Mono\", \"Courier New\", monospace', p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: (t)=> t.palette.mode==='dark'?'#0b1020':'#0f172a', color: '#e5e7eb' }}>
              {(() => {
                try {
                  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://www.askthebill.ai';
                  const api = `${origin}/api`;
                  const ident = `${congress}/${billType}/${billNumber}`;
                  const code = `<script>window.AskTheBill = { apiBase: '${api}' };</script>\n<script async src="${origin}/askthebill.widget.js"></script>\n<div data-askthebill=\"bill-status\" data-bill=\"${ident}\"></div>`;
                  return code;
                } catch { return ''; }
              })()}
            </Box>
            <Button size="small" variant="outlined" sx={{ position: 'absolute', top: 8, right: 8 }} onClick={() => {
              try {
                const origin = window.location.origin;
                const api = `${origin}/api`;
                const ident = `${congress}/${billType}/${billNumber}`;
                const code = `<script>window.AskTheBill = { apiBase: '${api}' };</script>\n<script async src="${origin}/askthebill.widget.js"></script>\n<div data-askthebill=\"bill-status\" data-bill=\"${ident}\"></div>`;
                if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(code);
              } catch {}
            }}>Copy</Button>
          </Box>
        </Paper>
      )}

      {/* Main two-column layout */}
      <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 2fr) minmax(0, 1fr)' }, gap: 2 }}>
        <Paper sx={{ order: 1, minWidth: 0 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ position: stickyOk ? 'sticky' : 'static', top: stickyOk ? 48 : undefined, zIndex: 10, bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}
          >
          <Tab id="bill-tab-0" aria-controls="bill-tabpanel-0" label="Overview" />
          <Tab id="bill-tab-1" aria-controls="bill-tabpanel-1" label="Actions & Timeline" />
          <Tab id="bill-tab-2" aria-controls="bill-tabpanel-2" label="Amendments" />
          <Tab id="bill-tab-3" aria-controls="bill-tabpanel-3" label="Full Text" />
          <Tab id="bill-tab-4" aria-controls="bill-tabpanel-4" label="Summaries" />
          <Tab id="bill-tab-5" aria-controls="bill-tabpanel-5" label="Subjects" />
          <Tab id="bill-tab-6" aria-controls="bill-tabpanel-6" label="Titles" />
          <Tab id="bill-tab-7" aria-controls="bill-tabpanel-7" label="Related Bills" />
          <Tab id="bill-tab-8" aria-controls="bill-tabpanel-8" label="Committees" />
          </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Typography variant="h5" component="h2" gutterBottom>Understand this bill</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Congress {currentBill.congress} · {(currentBill.bill_type || currentBill.type)?.toUpperCase()} {currentBill.bill_number || currentBill.number}
            {currentBill.introduced_date ? ` · Introduced ${new Date(currentBill.introduced_date).toLocaleDateString()}` : ''}
          </Typography>
        {(currentBill.latest_action_text || currentBill.latest_action_date) && (
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6">Latest Action</Typography>
              {currentBill.latest_action_text && (
                <Typography variant="body2" color="textSecondary">
                  {currentBill.latest_action_text}
                </Typography>
              )}
              {currentBill.latest_action_date && (
                <Typography variant="body2" color="textSecondary">
                  Date: {new Date(currentBill.latest_action_date).toLocaleDateString()}
                </Typography>
              )}
            </CardContent>
          </Card>
        )}

          {/* AI-generated Summary Section */}
          <Paper sx={{ p: 2, mt: 2 }}>
            <Typography variant="h6" gutterBottom>AI-generated Bill Summary</Typography>
            {aiGenError && (
              <Typography variant="body2" color="error" gutterBottom>
                {aiGenError}
              </Typography>
            )}
            {(() => {
              const s = (currentBill.ai_summary || '').trim();
              if (s) {
                return (
                  <Box sx={{
                    '& h1, & h2, & h3, & h4': { mt: 1.5, mb: 1 },
                    '& p': { mb: 1 },
                    '& ul, & ol': { pl: 3, mb: 1 },
                    '& li': { mb: 0.5 },
                    '& strong': { fontWeight: 600 },
                    '& code': { fontFamily: 'monospace', backgroundColor: 'rgba(0,0,0,0.04)', px: 0.5, borderRadius: 0.5 },
                    '& pre > code': { display: 'block', whiteSpace: 'pre-wrap' },
                  }}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {normalizeSummary(s)}
                    </ReactMarkdown>
                  </Box>
                );
              }
              return (
                <>
                  <Typography>An AI explanation is not available yet. Use Generate AI summary in the header, or read the official text and actions using the tabs above.</Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    The AI-generated summary will appear here once ready.
                  </Typography>
                </>
              );
            })()}
          </Paper>

          {/* Votes Section */}
          {Array.isArray((currentBill as any).votes) && (currentBill as any).votes.length > 0 && (
            <Paper sx={{ p: 2, mt: 2 }}>
              <Typography variant="h6" gutterBottom>Votes</Typography>
              <Stack spacing={1}>
                {((currentBill as any).votes || []).map((v: any, idx: number) => {
                  const t = (currentBill.bill_type || (currentBill as any).type || '').toString().toUpperCase();
                  const n = (currentBill.bill_number || (currentBill as any).number || '').toString();
                  const bid = [t, n].filter(Boolean).join(' ');
                  const dateStr = v.startDate || v.updateDate || v.date;
                  const billUrl = `/bills/${currentBill.congress}/${(currentBill.bill_type || (currentBill as any).type)}/${(currentBill.bill_number || (currentBill as any).number)}`;
                  return (
                    <VoteCard
                      key={idx}
                      date={dateStr}
                      billId={bid}
                      voteType={v.voteType || v.vote_type}
                      result={v.result}
                      rollCall={(v.rollCallNumber || v.roll_call_number || '').toString()}
                      billUrl={billUrl}
                    />
                  );
                })}
              </Stack>
            </Paper>
          )}

          {/* Sponsors Section */}
          {(() => {
            const hasSponsor = Boolean(currentBill.sponsors?.length || currentBill.sponsor_bioguide_id || currentBill.sponsor_name);
            const cos = Array.isArray(currentBill.cosponsors) && currentBill.cosponsors.length > 0
              ? currentBill.cosponsors
              : (billCosponsors?.cosponsors || []);
            const hasCosponsors = Array.isArray(cos) && cos.length > 0;
            if (!(hasSponsor || hasCosponsors)) return null;
            return (
              <Paper sx={{ p: 2, mt: 2 }}>
                {hasSponsor && (
                  <>
                    <Typography variant="h6" gutterBottom>Sponsor</Typography>
                    {currentBill.sponsors?.length ? (
                      <Button
                        variant="text"
                        onClick={() => {
                          const s = currentBill.sponsors[0];
                          const id = s.bioguide_id || s.bioguideId;
                          if (id) navigate(`/members/${id}`);
                        }}
                      >
                        {(() => {
                          const s = currentBill.sponsors[0];
                          const name = s.name || `${s.first_name || s.firstName || ''} ${s.last_name || s.lastName || ''}`.trim();
                          const party = s.party ? ` (${s.party}${s.state ? '-' + s.state : ''})` : (s.state ? ` (${s.state})` : '');
                          return `${name}${party}`;
                        })()}
                      </Button>
                    ) : (
                      <Button
                        variant="text"
                        onClick={() => {
                          if (currentBill.sponsor_bioguide_id) navigate(`/members/${currentBill.sponsor_bioguide_id}`);
                        }}
                      >
                        {currentBill.sponsor_name} {currentBill.sponsor_party ? `(${currentBill.sponsor_party}${currentBill.sponsor_state ? '-' + currentBill.sponsor_state : ''})` : ''}
                      </Button>
                    )}
                  </>
                )}

                {/* Cosponsors */}
                {hasCosponsors && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>Co-sponsors</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {cos.map((c: any, idx: number) => {
                        const id = c.bioguide_id || c.bioguideId;
                        const name = c.name || `${c.first_name || c.firstName || ''} ${c.last_name || c.lastName || ''}`.trim();
                        const party = c.party ? ` (${c.party}${c.state ? '-' + c.state : ''})` : (c.state ? ` (${c.state})` : '');
                        return (
                          <Chip
                            key={idx}
                            label={`${name}${party}`}
                            onClick={() => id && navigate(`/members/${id}`)}
                            variant="outlined"
                          />
                        );
                      })}
                    </Box>
                  </Box>
                )}
              </Paper>
            );
          })()}

          {currentBill.summary && (
            <Paper sx={{ p: 2, mt: 2 }}>
              <Typography variant="h6" gutterBottom>Summary</Typography>
              <Typography variant="body1" whiteSpace="pre-wrap">{currentBill.summary}</Typography>
            </Paper>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {billActions ? (
            <List>
              {billActions.actions?.map((action: any, index: number) => {
                const aText = action.text || action.action_text || action.actionText || action.description || 'No description';
                const aDate = action.action_date || action.actionDate || action.date || null;
                return (
                  <ListItem key={index} divider>
                    <ListItemText
                      primary={aText}
                      secondary={aDate ? new Date(aDate).toLocaleDateString() : 'No date'}
                    />
                  </ListItem>
                );
              }) || <Typography>No actions found</Typography>}
            </List>
          ) : (
            <LoadingSpinner message="Loading actions..." />
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {billAmendments ? (
            billAmendments.amendments && billAmendments.amendments.length > 0 ? (
              <List>
                {billAmendments.amendments.map((am: any, idx: number) => {
                  const aCongress = am.congress || currentBill?.congress || congress;
                  const aType = (am.type || '').toLowerCase();
                  const aNumber = String(am.number || '').trim();
                  const to = aCongress && aType && aNumber ? `/bills/${aCongress}/${aType}/${aNumber}` : undefined;
                  return (
                    <ListItem key={idx} divider>
                      {to ? (
                        <ListItemText
                          primary={<span style={{ textDecoration: 'underline' }}>{`${(am.type || '').toUpperCase()} ${am.number || ''}`.trim() || 'Amendment'}</span>}
                          secondary={am.description || am.purpose || 'No description'}
                          primaryTypographyProps={{ component: (props:any) => <a {...props} href={to as string} onClick={(e)=>{ e.preventDefault(); navigate(to); }} /> }}
                        />
                      ) : (
                        <ListItemText
                          primary={`${(am.type || '').toUpperCase()} ${am.number || ''}`.trim() || 'Amendment'}
                          secondary={am.description || am.purpose || 'No description'}
                        />
                      )}
                    </ListItem>
                  );
                })}
              </List>
            ) : (
              <Typography>No amendments found</Typography>
            )
          ) : (
            <LoadingSpinner message="Loading amendments..." />
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          {billText ? (
            <div>
              {billText.html ? (
                <Paper sx={{ p: 2, mb: 2 }}>
                  <Typography variant="h6" gutterBottom>Bill Text</Typography>
                  <div dangerouslySetInnerHTML={{ __html: billText.html }} />
                </Paper>
              ) : billText.text ? (
                <Paper sx={{ p: 2, mb: 2 }}>
                  <Typography variant="h6" gutterBottom>Bill Text</Typography>
                  <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, \"Liberation Mono\", \"Courier New\", monospace' }}>
                    {billText.text}
                  </pre>
                </Paper>
              ) : billText.pdf ? (
                <Paper sx={{ p: 2, mb: 2, height: 600 }}>
                  <Typography variant="h6" gutterBottom>Bill Text (PDF)</Typography>
                  <iframe
                    src={billText.pdf}
                    title="Bill PDF"
                    style={{ width: '100%', height: '100%', border: 'none' }}
                  />
                </Paper>
              ) : null}
              {billText.textVersions?.length ? (
                billText.textVersions.map((version: any, index: number) => (
                  <Card key={index} sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="h6">{version.type}</Typography>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        Date: {version.date ? new Date(version.date).toLocaleDateString() : 'No date'}
                      </Typography>
                      {/* Render available format links */}
                      {version.formats?.map((fmt: any, i: number) => (
                        <Typography key={i} variant="body2">
                          <a href={fmt.url} target="_blank" rel="noopener noreferrer">
                            {fmt.type || 'View'}
                          </a>
                        </Typography>
                      ))}
                    </CardContent>
                  </Card>
                ))
              ) : (
                !billText.html && !billText.text && !billText.pdf ? <Typography>No text versions found</Typography> : null
              )}
            </div>
          ) : (
            <LoadingSpinner message="Loading text versions..." />
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          {billSummaries ? (
            <div>
              {!billSummaries.summaries?.length && <Typography>No official summaries available.</Typography>}
              {billSummaries.summaries?.map((summary: any, index: number) => (
                <Card key={index} sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="h6">{summary.actionDesc}</Typography>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Date: {summary.actionDate ? new Date(summary.actionDate).toLocaleDateString() : 'No date'}
                    </Typography>
                    <Typography variant="body1">
                      {summary.text}
                    </Typography>
                  </CardContent>
                </Card>
              )) || <Typography>No summaries found</Typography>}
            </div>
          ) : (
            <LoadingSpinner message="Loading summaries..." />
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={5}>
          {billSubjects ? (
            <div>
              {billSubjects.subjects?.policyArea?.name && (
                <Typography variant="h6" gutterBottom>Policy Area: {billSubjects.subjects.policyArea.name}</Typography>
              )}
              {Array.isArray(billSubjects.subjects?.legislativeSubjects) && billSubjects.subjects.legislativeSubjects.length > 0 ? (
                <List>
                  {billSubjects.subjects.legislativeSubjects.map((s: any, idx: number) => (
                    <ListItem key={idx} divider>
                      <ListItemText primary={s.name} secondary={s.updateDate ? new Date(s.updateDate).toLocaleString() : undefined} />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography>No subjects found</Typography>
              )}
            </div>
          ) : (
            <LoadingSpinner message="Loading subjects..." />
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={6}>
          {billTitles ? (
            Array.isArray(billTitles.titles) && billTitles.titles.length > 0 ? (
              <List>
                {billTitles.titles.map((t: any, idx: number) => (
                  <ListItem key={idx} divider>
                    <ListItemText primary={t.title} secondary={`${t.titleType || ''}${t.chamberName ? ' • ' + t.chamberName : ''}${t.updateDate ? ' • ' + new Date(t.updateDate).toLocaleString() : ''}`} />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography>No titles found</Typography>
            )
          ) : (
            <LoadingSpinner message="Loading titles..." />
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={7}>
          {billRelatedBills ? (
            Array.isArray(billRelatedBills.relatedBills) && billRelatedBills.relatedBills.length > 0 ? (
              <List>
                {billRelatedBills.relatedBills.map((rb: any, idx: number) => (
                  <ListItem key={idx} divider button onClick={() => navigate(`/bills/${rb.congress}/${(rb.type || '').toLowerCase()}/${rb.number}`)}>
                    <ListItemText
                      primary={`${(rb.type || '').toUpperCase()} ${rb.number} — ${rb.title || ''}`}
                      secondary={rb.latestAction?.text || ''}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography>No related bills found</Typography>
            )
          ) : (
            <LoadingSpinner message="Loading related bills..." />
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={8}>
          {billCommittees ? (
            Array.isArray(billCommittees.committees) && billCommittees.committees.length > 0 ? (
              <List>
                {billCommittees.committees.map((c: any, idx: number) => (
                  <ListItem key={idx} divider>
                    <ListItemText
                      primary={`${c.name || ''} ${c.chamber ? '— ' + c.chamber : ''}`}
                      secondary={Array.isArray(c.activities) ? c.activities.map((a: any) => `${a.name}${a.date ? ' (' + new Date(a.date).toLocaleDateString() + ')' : ''}`).join(' • ') : undefined}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography>No committees found</Typography>
            )
          ) : (
            <LoadingSpinner message="Loading committees..." />
          )}
        </TabPanel>
        </Paper>

        {/* Right column: AI + Engagement */}
        <Box sx={{ order: 2, minWidth: 0 }}>
          {/* Ask AI Card */}
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6">Ask AI about this bill</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Get answers based on the official text and actions of {(currentBill.bill_type || currentBill.type || '').toUpperCase()} {currentBill.bill_number || currentBill.number}.
            </Typography>
            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
              {[
                "What does this bill actually do?",
                "Who could be affected by this bill?",
                "What's the difference between this and current law?",
              ].map((q) => (
                <Chip key={q} label={q} onClick={() => {
                  const type = (currentBill.bill_type || currentBill.type || '').toLowerCase();
                  const number = String(currentBill.bill_number || currentBill.number || '').trim();
                  navigate('/chat', { state: { billContext: [`${currentBill.congress}:${type}:${number}`], initialQuestion: q } });
                }} />
              ))}
            </Stack>
            <Button fullWidth sx={{ mt: 1.5 }} variant="contained" onClick={() => {
              const type = (currentBill.bill_type || currentBill.type || '').toLowerCase();
              const number = String(currentBill.bill_number || currentBill.number || '').trim();
              navigate('/chat', { state: { billContext: [`${currentBill.congress}:${type}:${number}`] } });
            }}>Open Chat</Button>
          </Paper>

        </Box>
      </Box>

      <Box component="section" aria-label="Your notes and community" sx={{ mt: 3 }}>
        <Typography variant="h5" component="h2" sx={{ mb: 2 }}>Your notes & community</Typography>
        {/* Ad Slot: contextual to this bill (uses subjects/tags if available) */}
        <Box sx={{ my: 2, display: 'flow-root' }}>
          <AdSlot
            slotId={`ad-bill-${currentBill.congress}-${(currentBill.bill_type || currentBill.type)}-${(currentBill.bill_number || currentBill.number)}`}
            width={320}
            height={100}
            tags={Array.isArray(billSubjects?.subjects?.legislativeSubjects) ? billSubjects.subjects.legislativeSubjects.map((s:any)=>s.name).slice(0,5) : []}
            pageType="bill-detail"
          />
        </Box>

        {/* Community Poll */}
        {poll && (
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>Community Poll</Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>{poll.question}</Typography>
            {pollError && <Typography color="error" variant="body2">{pollError}</Typography>}
            {pollLoading ? (
              <Typography variant="body2" color="textSecondary">Loading…</Typography>
            ) : (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                {['support','oppose','unsure'].map((opt) => (
                  <Button key={opt} variant={poll.userChoice === opt ? 'contained' : 'outlined'} size="small" onClick={async () => {
                    if (!authenticated) { login(); return; }
                    try {
                      const resp = await apiService.voteBillPoll(parseInt(congress as string), billType as string, billNumber as string, opt as any);
                      setPoll(resp?.data || resp);
                    } catch (e) {
                      // ignore for now
                    }
                  }}>
                    {opt === 'support' ? 'Support' : opt === 'oppose' ? 'Oppose' : 'Unsure'}
                  </Button>
                ))}
              </Stack>
            )}
            {poll && poll.counts && (
              <Box sx={{ mt: 1 }}>
                <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
                  {Object.keys(poll.counts).map((k: string) => (
                    <Chip key={k} size="small" label={`${k}: ${poll.counts[k]}`} />
                  ))}
                  <Chip size="small" variant="outlined" label={`Total: ${poll.total || 0}`} />
                </Stack>
              </Box>
            )}
          </Paper>
        )}

        {/* Notes */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>My Notes</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Drafts stay on this device. Sign in and save to keep a note in your account.</Typography>
          {noteSaved && <Typography variant="body2" color="success.main" sx={{ mb: 1 }}>{noteSaved}</Typography>}
          {noteError && <Typography variant="body2" color="error" sx={{ mb: 1 }}>{noteError}</Typography>}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
            <TextField
              label="Add a note to this bill"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              multiline
              minRows={2}
              fullWidth
            />
            <Button
              variant="contained"
              onClick={async () => {
                if (!authenticated) { login(); return; }
                setNoteError(null); setNoteSaved(null);
                try {
                  await apiService.createNoteForTarget({ target_type: 'bill', target_ref: `${congress}/${billType}/${billNumber}`, text: noteText.trim() });
                  setNoteSaved('Saved to your account');
                  setNoteText('');
                } catch (e: any) {
                  setNoteError(e?.response?.data?.detail || 'Failed to save note');
                }
              }}
              disabled={!noteText.trim()}
            >
              Save Note
            </Button>
          </Stack>
        </Paper>


      {/* Reactions Bar */}
      {socialUnavailable.reactions !== true && (
        <Box sx={{ mt: 1, mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle2">Reactions:</Typography>
            {['like','dislike','laugh','love','insightful'].map((rt) => (
              <Tooltip key={rt} title={rt}>
                <IconButton size="small" onClick={async () => {
                  try {
                    const bid = (currentBill as any)?.bill_id;
                    if (!bid) return;
                    await apiService.addReaction('bill', Number(bid), rt);
                    const sum = await apiService.getReactionSummary('bill', Number(bid));
                    setReactionSummary((sum.data && sum.data.summary) || {});
                  } catch (e: any) {
                    if (e?.response?.status === 404) setSocialUnavailable(prev => ({ ...prev, reactions: true }));
                  }
                }}>
                  <span style={{ fontSize: 18 }}>{rt === 'like' ? '👍' : rt === 'dislike' ? '👎' : rt === 'laugh' ? '😂' : rt === 'love' ? '❤️' : '💡'}</span>
                </IconButton>
              </Tooltip>
            ))}
            {reactionSummary && (
              <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                {(() => {
                  const rs: any = reactionSummary || {};
                  const fmt = (k: string, emoji: string) => rs && typeof rs[k] === 'number' ? `${emoji} ${rs[k]}` : `${emoji} 0`;
                  return [
                    fmt('like', '👍'),
                    fmt('dislike', '👎'),
                    fmt('laugh', '😂'),
                    fmt('love', '❤️'),
                    fmt('insightful', '💡'),
                  ].join('  ');
                })()}
              </Typography>
            )}
          </Stack>
        </Box>
      )}

      </Box>

      {/* Discussion */}
      {socialUnavailable.comments !== true && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography variant="h6" gutterBottom>Discussion</Typography>
          {Array.isArray(comments) ? (
            comments.length > 0 ? (
              <List>
                {comments.map((c: any) => (
                  <ListItem key={c.id} alignItems="flex-start" secondaryAction={authenticated && c.user_id ? (
                    <Button size="small" color="error" onClick={async () => {
                      try {
                        await apiService.deleteComment(c.id);
                        const bid = (currentBill as any)?.bill_id;
                        if (bid) {
                          const r = await apiService.listComments(Number(bid));
                          setComments(Array.isArray(r.data) ? r.data : (Array.isArray(r.data?.items) ? r.data.items : []));
                        }
                      } catch (e) {/* no-op */}
                    }}>Delete</Button>
                  ) : null}>
                    <ListItemText
                      primary={<Typography variant="body2">{c.body}</Typography>}
                      secondary={c.created_at ? new Date(c.created_at).toLocaleString() : null}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography color="textSecondary">No comments yet.</Typography>
            )
          ) : (
            <Typography color="textSecondary">Loading comments…</Typography>
          )}
          <Box sx={{ mt: 2 }}>
            {authenticated ? (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start">
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Write a comment…"
                  value={commentBody}
                  onChange={(e) => setCommentBody(e.target.value)}
                />
                <Button
                  variant="contained"
                  disabled={commentPosting || !commentBody.trim()}
                  onClick={async () => {
                    const bid = (currentBill as any)?.bill_id;
                    if (!bid || !commentBody.trim()) return;
                    try {
                      setCommentPosting(true);
                      await apiService.createComment(Number(bid), commentBody.trim());
                      setCommentBody('');
                      const r = await apiService.listComments(Number(bid));
                      setComments(Array.isArray(r.data) ? r.data : (Array.isArray(r.data?.items) ? r.data.items : []));
                    } catch (e: any) {
                      if (e?.response?.status === 404) setSocialUnavailable(prev => ({ ...prev, comments: true }));
                    } finally {
                      setCommentPosting(false);
                    }
                  }}
                >Post</Button>
              </Stack>
            ) : (
              <Typography variant="body2" color="textSecondary">Sign in to comment.</Typography>
            )}
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default BillDetail;
